/**
 * PWS Franchisee Presentation Deck (Feb 2026)
 * =========================================
 *
 * Full-bleed, single-scroll sales presentation for prospective PWS
 * franchisees. Also renders cleanly in fullscreen mode (`F` key) for
 * projector / screen-share pitches and prints one section per A4 page
 * as a paper handout (see the `@page` block below).
 *
 * Routes:
 *   - Public view-only:  /pws-partner-presentation
 *   - Admin entry:       /admin/partners/pws-presentation
 *     (same component; the admin entry just gets the "Copy share link"
 *      button shown by default via `?admin=1` URL flag — no separate
 *      component tree needed.)
 *
 * Keyboard shortcuts:
 *   - ArrowRight / Space / PageDown : next section
 *   - ArrowLeft  / PageUp           : prev section
 *   - Home / End                    : first / last section
 *   - F                             : toggle fullscreen
 *   - Esc                           : exit fullscreen
 *
 * Sections (11 total, keep in sync with SECTIONS below):
 *   1. Title
 *   2. Australia's water testing opportunity
 *   3. The AWARE laboratory
 *   4. How the partnership works (partner-side)
 *   5. Customer Journey (mail-in booking flow)
 *   6. What the customer receives (branded report)
 *   7. What you earn
 *   8. Support & tools
 *   9. Onboarding roadmap
 *   10. Testimonials placeholder
 *   11. Ready to join?
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Maximize2, Minimize2, ChevronLeft, ChevronRight, Link2, FileText,
  Users, Droplets, Beaker, Compass, TrendingUp, Award, Handshake,
  Sparkles, Quote, CheckCircle2, Menu, X,
  MapPin, Calendar, Mail, Package, TestTube, Truck, Send,
} from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";

const SECTIONS = [
  { key: "title",         label: "Welcome",           icon: Sparkles },
  { key: "opportunity",   label: "The Opportunity",   icon: TrendingUp },
  { key: "lab",           label: "The Lab",           icon: Beaker },
  { key: "workflow",      label: "How It Works",      icon: Compass },
  { key: "customer_journey", label: "Customer Journey", icon: MapPin },
  { key: "report",        label: "The Report",        icon: FileText },
  { key: "revenue",       label: "What You Earn",     icon: Droplets },
  { key: "support",       label: "Support & Tools",   icon: Handshake },
  { key: "roadmap",       label: "Onboarding",        icon: Award },
  { key: "testimonials",  label: "Testimonials",      icon: Quote },
  { key: "cta",           label: "Join Us",           icon: Users },
];

const HERO_LAB_IMG =
  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/o53d9j1l_ChatGPT%20Image%20Jul%209%2C%202026%2C%2004_20_57%20PM.png";
const WORKFLOW_IMG =
  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/akylmo53_AWARE%20Partner%20Image.png";
// Feb 2026 — user-supplied title-slide logo (uploaded via chat assets).
const TITLE_LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/jhugp9s5_image.png";
// Feb 2026 — Posted Water Test Kit hero image (kit tube + sterile bottle,
// step-by-step instruction card, prepaid Express Post return satchel and
// return envelope) — used inside the Customer Journey section to show
// franchisees exactly what lands on the customer's doormat.
const POSTED_KIT_IMG =
  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/33a6ivui_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2010_00_37%20PM.png";
// Feb 2026 — user-supplied composite showing the branded COA (AWARE)
// side-by-side with the branded Summary & Recommendations (PWS Partner).
// Used inside the Customer Experience slide (report renderer).
const REPORT_PREVIEW_IMG =
  "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/z6ak450q_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2010_21_35%20PM.png";

// Motion presets — one shared fade-up so every section transitions
// consistently without listing the same object literal ten times.
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function PwsPartnerPresentation() {
  const [params] = useSearchParams();
  const isAdminEntry = params.get("admin") === "1"
    || (typeof window !== "undefined" && window.location.pathname.startsWith("/admin"));
  const [current, setCurrent] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [isFullscreen, setFullscreen] = useState(false);
  const sectionRefs = useRef([]);

  const scrollTo = (idx) => {
    const clamped = Math.max(0, Math.min(SECTIONS.length - 1, idx));
    setCurrent(clamped);
    sectionRefs.current[clamped]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTocOpen(false);
  };

  // Update current section index as the user scrolls manually.
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Number(e.target.dataset.idx);
            if (!Number.isNaN(idx)) setCurrent(idx);
          }
        });
      },
      { threshold: 0.5 },
    );
    sectionRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  // Keyboard nav.
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); scrollTo(current + 1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); scrollTo(current - 1); }
      else if (e.key === "Home") { e.preventDefault(); scrollTo(0); }
      else if (e.key === "End") { e.preventDefault(); scrollTo(SECTIONS.length - 1); }
      else if (e.key.toLowerCase() === "f") { e.preventDefault(); toggleFullscreen(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current]);

  // Track native fullscreen changes so the toggle icon stays accurate
  // even if the user presses Esc.
  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/pws-partner-presentation`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public presentation link copied", { description: url });
    } catch {
      toast.info("Copy manually", { description: url });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pws-presentation" data-testid="pws-presentation-root">
      {/* Print-only stylesheet: one section per A4 page as a handout */}
      <style>{`
        @media print {
          .pws-presentation .pws-chrome { display: none !important; }
          .pws-presentation .pws-section { page-break-after: always; min-height: 297mm; }
          .pws-presentation { background: white !important; color: #0f172a !important; }
        }
      `}</style>

      {/* Chrome — top bar with logo, TOC toggle, admin share button, fullscreen */}
      <header className={`pws-chrome fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-slate-950/70 border-b border-slate-800 transition-opacity ${isFullscreen ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setTocOpen(true)}
              data-testid="pws-presentation-toc-toggle"
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
              aria-label="Open section menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <BrandLogo variant="dark" className="h-7" />
            <span className="text-xs text-slate-400 hidden sm:inline">Franchisee Presentation</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 tabular-nums" data-testid="pws-presentation-section-counter">
              {current + 1} / {SECTIONS.length}
            </span>
            {isAdminEntry && (
              <button
                type="button"
                onClick={copyShareLink}
                data-testid="pws-presentation-copy-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" />
                Copy share link
              </button>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              data-testid="pws-presentation-fullscreen"
              title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen (F)"}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
              aria-label="Toggle fullscreen"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Table-of-contents drawer */}
      <TocDrawer open={tocOpen} onClose={() => setTocOpen(false)} onJump={scrollTo} current={current} />

      {/* Slide/section stack */}
      <main className="pt-16">
        {SECTIONS.map((s, idx) => (
          <section
            key={s.key}
            ref={(el) => (sectionRefs.current[idx] = el)}
            data-idx={idx}
            data-testid={`pws-presentation-section-${s.key}`}
            className="pws-section min-h-screen flex items-center justify-center px-4 sm:px-8 py-16 sm:py-24 relative"
          >
            {SECTION_RENDERERS[s.key]()}
          </section>
        ))}
      </main>

      {/* Bottom nav — hidden in fullscreen */}
      <nav className={`pws-chrome fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md bg-slate-950/70 border-t border-slate-800 transition-opacity ${isFullscreen ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => scrollTo(current - 1)}
            disabled={current === 0}
            data-testid="pws-presentation-prev"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-800 text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="flex-1 mx-4 h-1 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-cyan-500 transition-all duration-500"
              style={{ width: `${((current + 1) / SECTIONS.length) * 100}%` }}
            />
          </div>
          <button
            type="button"
            onClick={() => scrollTo(current + 1)}
            disabled={current === SECTIONS.length - 1}
            data-testid="pws-presentation-next"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-800 text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </nav>
    </div>
  );
}

function TocDrawer({ open, onClose, onJump, current }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 pws-chrome" data-testid="pws-presentation-toc">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="absolute left-0 top-0 bottom-0 w-80 bg-slate-900 border-r border-slate-800 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="text-sm font-semibold text-slate-300">Sections</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pws-presentation-toc-close"
            className="p-1.5 rounded-lg hover:bg-slate-800"
            aria-label="Close section menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="py-2">
          {SECTIONS.map((s, idx) => {
            const Icon = s.icon;
            const active = idx === current;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => onJump(idx)}
                  data-testid={`pws-presentation-toc-item-${s.key}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${active ? "bg-cyan-600/20 text-cyan-100 border-l-2 border-cyan-500" : "hover:bg-slate-800 text-slate-300"}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{idx + 1}. {s.label}</span>
                  {active && <span className="text-[10px] uppercase tracking-wider text-cyan-400">Now</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
}

/* ================================================================
   Section renderers — one per slide. Kept as small pure functions
   so the section stack is a simple map + refs pattern.
   ================================================================ */

const SECTION_RENDERERS = {
  title: () => (
    <div className="max-w-6xl mx-auto text-center flex flex-col justify-center">
      <motion.div {...fadeUp}>
        {/* Feb 2026 — AWARE logo on the dark title slide. Both the
            CDN and public/brand transparent PNGs have a checker
            pattern baked into the "transparent" pixels, so we wrap
            the img in a white rounded card (like the site header)
            to hide the checkerboard. Generous mb-20 keeps the card
            well clear of the eyebrow tagline below. */}
        <div className="mx-auto inline-flex items-center justify-center bg-white rounded-3xl px-14 py-10 sm:px-20 sm:py-14 shadow-2xl mb-20">
          <img
            src={TITLE_LOGO_IMG}
            alt="AWARE Water Testing"
            className="h-36 sm:h-44 lg:h-52 w-auto object-contain"
            draggable={false}
            data-testid="pws-presentation-title-logo"
          />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-4">AWARE — Australian Water Analysis, Reporting &amp; Evaluation</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
          Become an <span className="text-cyan-400">AWARE PWS</span> Partner
        </h1>
        <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
          Join Australia&apos;s growing network of independent Pristine Water Systems
          franchisees, backed by an Australian testing laboratory and plain-English
          reporting your customers actually read.
        </p>
        <div className="mt-12 text-xs text-slate-500">Press <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700">→</kbd> or <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700">Space</kbd> to advance · <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700">F</kbd> for fullscreen</div>
      </motion.div>
    </div>
  ),

  opportunity: () => (
    <div className="max-w-6xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">The Opportunity</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">Private water quality is often overlooked in Australia.</h2>
        <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-8">
          Private water quality deserves greater attention across Australia.
        </p>

        {/* Feb 2026 — three-card evidence grid. Copy intentionally
            credible and specific to *private* water supplies (tank
            &amp; bore), so it doesn't cast doubt on Australia's
            regulated mains-water system. */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
            <div className="font-display text-4xl font-bold text-cyan-400 mb-2">Millions</div>
            <div className="font-display text-base font-bold text-white mb-1.5">of Australian households</div>
            <div className="text-slate-300 text-sm leading-snug">
              use rainwater tanks or other private water supplies as a primary
              or supplementary drinking-water source.
            </div>
          </div>
          <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
            <div className="font-display text-3xl sm:text-4xl font-bold text-cyan-400 mb-2 leading-tight">Bacteria can be present</div>
            <div className="text-slate-300 text-sm leading-snug">
              Australian studies have detected <em>E. coli</em> and other
              bacterial contamination in a significant proportion of sampled
              rainwater tanks.
            </div>
          </div>
          <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
            <div className="font-display text-3xl sm:text-4xl font-bold text-cyan-400 mb-2 leading-tight">Testing is essential</div>
            <div className="text-slate-300 text-sm leading-snug">
              Tank and bore water quality can change over time — even when the
              water looks, tastes and smells normal.
            </div>
          </div>
        </div>

        <p className="mt-8 text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed">
          Most households on rain-fed tanks, bore water or off-grid supply go
          without regular testing because it feels expensive, confusing or
          out-of-reach. That&apos;s the gap AWARE fills — and it&apos;s the gap a PWS
          partnership lets you monetise on your side.
        </p>
      </motion.div>
    </div>
  ),

  lab: () => (
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">The AWARE Lab</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">Australia-wide testing, from one Noosaville lab.</h2>
        <ul className="space-y-4 text-slate-300 text-lg">
          <Li>Laboratory methodologies aligned with the techniques used by council labs.</Li>
          <Li>Bacterial, chemical and heavy-metal panels tuned for Australian tank &amp; bore water.</Li>
          <Li>Samples posted to us from anywhere in the country return results within 1–3 business days.</Li>
          <Li>Every result is turned into a plain-English report the customer actually reads.</Li>
        </ul>
      </motion.div>
      <motion.img
        src={HERO_LAB_IMG}
        alt="Inside the AWARE laboratory"
        className="rounded-2xl shadow-2xl border border-slate-800"
        {...fadeUp}
      />
    </div>
  ),

  workflow: () => (
    <div className="max-w-7xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">How It Works</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">A three-step handover, from your van to our lab.</h2>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <img
            src={WORKFLOW_IMG}
            alt="PWS + AWARE partner workflow"
            className="w-full rounded-2xl shadow-2xl border border-slate-800 max-h-[55vh] object-contain bg-slate-900"
          />
          <div className="space-y-4">
            <Step n="01" title="Sample handover" body="You collect the sample during your regular tank clean or plumbing visit and hand it back with the pre-printed AWARE label." />
            <Step n="02" title="Post to the lab" body="Drop the parcel into any Australia Post box using the prepaid Express Post label — no separate trip needed." />
            <Step n="03" title="Report to customer" body="Your customer receives a branded plain-English report in their inbox, with your name on the masthead." />
          </div>
        </div>
      </motion.div>
    </div>
  ),

  customer_journey: () => (
    <div className="max-w-7xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-2">The Customer Journey</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
          From discovery to results — every step attributed to <span className="text-cyan-400">your</span> territory.
        </h2>
        <p className="text-slate-300 text-sm sm:text-base max-w-4xl mb-6 leading-snug">
          Customers book via your partner link or general search — our postcode lookup auto-credits every sale to your PWS territory.
        </p>

        {/* Feb 2026 — Posted Kit hero row. Shows franchisees exactly
            what a customer receives on their doormat. Image column is
            capped by max-h so the row stays within the single-slide
            viewport alongside the 7-step grid below. */}
        <div className="grid lg:grid-cols-5 gap-4 lg:gap-6 items-stretch mb-5">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden bg-white border border-slate-800 shadow-2xl flex items-center justify-center">
            <img
              src={POSTED_KIT_IMG}
              alt="AWARE Posted Water Test Kit — kit tube, sterile bottle, instruction card, prepaid Express Post label and return envelope"
              className="w-full h-full max-h-[38vh] object-contain"
              data-testid="pws-presentation-journey-kit-image"
            />
          </div>
          <div className="lg:col-span-2 bg-slate-900 rounded-2xl border border-slate-800 p-4 sm:p-5">
            <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">What lands on their doormat</div>
            <div className="font-display text-lg sm:text-xl font-bold mb-2.5">Everything they need, prepaid.</div>
            <ul className="space-y-1.5 text-slate-300 text-xs sm:text-sm">
              <Li small>AWARE-branded kit tube, pre-addressed</Li>
              <Li small>Sterile sample bottle + zip-lock transport bag</Li>
              <Li small>Step-by-step instruction card</Li>
              <Li small>Prepaid Australia Post <strong>Express Post</strong> return label</Li>
              <Li small>Reusable Postal-Tube to return to the lab</Li>
            </ul>
          </div>
        </div>

        {/* 7-step journey grid — 4×2 on desktop. Compact copy keeps the
            whole section on one slide. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
          <JourneyStep n="01" icon={MapPin} title="Discovery" body="Partner link, QR or search. Postcode auto-attribution." />
          <JourneyStep n="02" icon={Calendar} title="Books online" body="2-minute checkout. Mail-in kit pathway Australia-wide." />
          <JourneyStep n="03" icon={Mail} title="Confirmation email" body="Branded receipt with order details and next steps." />
          <JourneyStep n="04" icon={Package} title="Kit posted" body="Drop-shipped to their door with prepaid return." />
          <JourneyStep n="05" icon={TestTube} title="Sample collected" body="Guided instructions on the card." />
          <JourneyStep n="06" icon={Truck} title="Returns to lab" body="Dropped in any Post box — arrives 1–3 business days." />
          <JourneyStep n="07" icon={Send} title="Results emailed" body="Summary + full COA sent to customer AND you." />
          <div className="bg-gradient-to-br from-cyan-950 to-slate-900 rounded-2xl p-3 sm:p-4 border border-cyan-900/50 flex flex-col justify-center">
            <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Your win</div>
            <div className="font-display text-sm sm:text-base font-bold mb-1">Commission on every kit</div>
            <div className="text-slate-300 text-[11px] sm:text-xs leading-snug">
              Every attributed test lands in your monthly partner statement.
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  ),

  report: () => (
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">The Customer Experience</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-6">Two reports, two brands, one clean handover.</h2>
        <p className="text-lg text-slate-300 leading-relaxed mb-6">
          When results are ready, every customer receives <strong>two</strong> PDFs
          side-by-side. The <span className="text-cyan-400 font-semibold">Certificate
          of Analysis</span> is always branded as <strong>AWARE Water Testing</strong> — that&apos;s
          the accredited lab document. The <span className="text-cyan-400 font-semibold">Plain-English
          Summary</span> is the customer-facing action plan and carries <strong>your PWS
          Partner logo and contact details</strong> on the masthead — so every follow-up
          call comes back to you.
        </p>

        {/* Two-column mini legend: which report is which brand. Keeps
            the split crystal clear for franchisees who might otherwise
            assume both PDFs are white-labelled. */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Lab document</div>
            <div className="font-display text-base font-bold text-white mb-1">Certificate of Analysis</div>
            <div className="text-xs text-slate-400 leading-snug">Always branded <span className="text-cyan-400 font-semibold">AWARE Water Testing</span>. The raw lab data + methodology, signed by the lab.</div>
          </div>
          <div className="bg-gradient-to-br from-cyan-950 to-slate-900 rounded-xl border border-cyan-900/50 p-4">
            <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold mb-1">Customer-facing document</div>
            <div className="font-display text-base font-bold text-white mb-1">Plain-English Summary</div>
            <div className="text-xs text-slate-300 leading-snug">Branded with <span className="text-cyan-400 font-semibold">your PWS logo, phone and email</span>. The action plan they&apos;ll call you about.</div>
          </div>
        </div>

        <ul className="space-y-3 text-slate-300">
          <Li>Your logo, phone and email on every Summary masthead.</Li>
          <Li>Traffic-light recommendations customers actually action.</Li>
          <Li>Follow-up service work routes straight back to your business.</Li>
          <Li>Suitable for insurance, sale of property, or peace of mind.</Li>
        </ul>
        <div className="mt-8">
          <a
            href="/api/reports/pristine-summary/sample"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pws-presentation-view-sample"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm transition-colors"
          >
            <FileText className="w-4 h-4" />
            View a sample report
          </a>
        </div>
      </motion.div>
      <motion.div {...fadeUp} className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-2xl">
        {/* Feb 2026 — placeholder while awaiting the actual COA +
            Summary composite image. Replace the src below with the
            URL of the customer-supplied artwork once received. */}
        {REPORT_PREVIEW_IMG ? (
          <img
            src={REPORT_PREVIEW_IMG}
            alt="Sample Certificate of Analysis and plain-English Summary Report"
            className="w-full rounded-lg bg-white"
          />
        ) : (
          <div className="w-full aspect-[3/4] rounded-lg bg-slate-800 border-2 border-dashed border-slate-700 flex items-center justify-center text-center p-8">
            <div>
              <FileText className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <div className="text-slate-400 text-sm font-medium">COA + Summary preview image</div>
              <div className="text-slate-500 text-xs mt-2">Set <code className="text-cyan-400">REPORT_PREVIEW_IMG</code> at the top of<br/><code className="text-cyan-400">PwsPartnerPresentation.jsx</code> to your artwork URL.</div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  ),

  revenue: () => (
    <div className="max-w-6xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">What You Earn</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">Revenue that stacks on top of your existing work.</h2>
        <p className="text-slate-300 text-base sm:text-lg max-w-4xl mb-6 leading-snug">
          Water testing isn&apos;t just a stand-alone product — it&apos;s the lead-in
          conversation for every follow-up job you already do best.
        </p>

        {/* Feb 2026 — Revenue math the user actually pitches with:
            10-20 tests/mo × 10% conversion = 1-2 additional service jobs,
            avg job ~$2,200 → an estimated $2,200-$4,400/mo of extra
            follow-up opportunity on top of test commissions. */}
        <div className="grid md:grid-cols-3 gap-3 sm:gap-4 mb-5">
          <div className="bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-800">
            <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Typical territory</div>
            <div className="font-display text-3xl sm:text-4xl font-bold mb-1">10–20</div>
            <div className="text-slate-400 text-xs">water tests booked per month</div>
          </div>
          <div className="bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-800">
            <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Conservative conversion</div>
            <div className="font-display text-3xl sm:text-4xl font-bold mb-1">10%</div>
            <div className="text-slate-400 text-xs">→ 1–2 follow-up service jobs / month</div>
          </div>
          <div className="bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-800">
            <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Follow-up job value</div>
            <div className="font-display text-3xl sm:text-4xl font-bold mb-1">$400 – $4,000</div>
            <div className="text-slate-400 text-xs">avg. potential job value ~$2,200</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-950 to-slate-900 rounded-2xl p-5 sm:p-6 border border-cyan-900/50 mb-3">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* LEFT — Test commissions alone (~$40-65 per test × 10-20 tests) */}
            <div className="md:pr-6 md:border-r md:border-cyan-900/40">
              <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Test commissions alone</div>
              <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-none">$400 – $1,300</div>
              <div className="text-slate-300 text-xs sm:text-sm mt-1">per month, from 10–20 tests at $40–$65 commission each.</div>
            </div>
            {/* RIGHT — Follow-up service opportunity (10% conversion, avg $2.2k) */}
            <div>
              <div className="text-cyan-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Additional follow-up opportunity</div>
              <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-none">$2,200 – $4,400</div>
              <div className="text-slate-300 text-xs sm:text-sm mt-1">from 1–2 service jobs (tank cleaning, filtration, UV, pH, servicing).</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-cyan-900/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <div className="text-cyan-300 text-[10px] font-semibold uppercase tracking-wider">Combined monthly earning potential</div>
            <div className="font-display text-2xl sm:text-3xl font-bold text-cyan-400">$2,600 – $5,700 / month</div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-800">
          <div className="text-xs sm:text-sm font-semibold text-slate-200 mb-2">Zero upfront cost, no monthly fees.</div>
          <ul className="space-y-1.5 text-slate-300 text-xs sm:text-sm">
            <Li small>No inventory — kits are drop-shipped to your customers directly by us.</Li>
            <Li small>No card processing — the customer pays us, we route commissions to you.</Li>
            <Li small>Itemised monthly statements so you always know where your money is coming from.</Li>
          </ul>
          <div className="mt-3 text-[11px] text-slate-500 italic">
            Figures shown are estimates based on typical territory activity and are not a guarantee of income. Actual results will vary by region, marketing effort and service mix.
          </div>
        </div>
      </motion.div>
    </div>
  ),

  support: () => (
    <div className="max-w-6xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">Support &amp; Tools</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-10">Everything you need to grow the water-testing side of your business.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card icon={Compass} title="Per-franchisee landing page" body="Your own branded page on awarewatertesting.com.au with QR code, service area map, and a lead-capture form." />
          <Card icon={Sparkles} title="Marketing kit" body="Van decals, brochures, business-card slots, social templates — all printable, all AWARE + your branding." />
          <Card icon={FileText} title="Sample kit fulfilment" body="Prepaid Express Post kits shipped to customers on your behalf — you never touch the postage layer." />
          <Card icon={TrendingUp} title="Attribution tracking" body="See every test attributed to your territory, every commission owed, every renewing customer." />
          <Card icon={Handshake} title="Direct lab contact" body="Text or call the lab manager for time-sensitive samples or awkward field questions." />
          <Card icon={Users} title="Franchisee community" body="Quarterly video roundtables, shared best-practice playbook, product roadmap input." />
        </div>
      </motion.div>
    </div>
  ),

  roadmap: () => (
    <div className="max-w-5xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">Your First 90 Days</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-10">From application to your first commission.</h2>
        <div className="space-y-6">
          <Phase weeks="Week 1" title="Application &amp; onboarding call" body="You apply. We call to make sure the territory is a fit. Branding materials are prepared." />
          <Phase weeks="Week 2–3" title="Setup" body="Your landing page goes live. Marketing kit shipped. Sample kit workflow demoed. Your team trained on the collection procedure." />
          <Phase weeks="Week 4" title="First tests" body="Start offering testing on every relevant service call. Samples fly to us. First reports issued under your branding." />
          <Phase weeks="Week 8–12" title="Momentum" body="First commission run pays out. Testimonials collected. We schedule the quarterly roundtable check-in." />
        </div>
      </motion.div>
    </div>
  ),

  testimonials: () => (
    <div className="max-w-5xl mx-auto w-full">
      <motion.div {...fadeUp}>
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">In Their Own Words</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mb-10">What our partners are saying.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Testimonial name="PWS Noosa" region="Sunshine Coast, QLD" quote="Adding testing to every tank-clean visit was a lay-down misère. Customers thank us for it." />
          <Testimonial name="PWS Regional Vic" region="Central Victoria" quote="The branded reports made us look ten times more professional overnight. Repeat customers went up 40%." />
        </div>
        <div className="mt-8 text-xs text-slate-500 italic text-center">
          [ Placeholder — real testimonials to be swapped in as partners come online. ]
        </div>
      </motion.div>
    </div>
  ),

  cta: () => (
    <div className="max-w-6xl mx-auto">
      <motion.div {...fadeUp} className="text-center mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">The PWS × AWARE Partner Network</div>
        <h2 className="font-display text-5xl sm:text-6xl font-bold mb-4">Let&apos;s talk.</h2>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
          Scan below to receive the digital Partner Guide and register your interest — we&apos;ll follow up within two business days
          to walk through territory, fit and next steps.
        </p>
      </motion.div>

      <motion.div
        {...fadeUp}
        className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-8 md:gap-12 items-center"
      >
        {/* Left: existing conference CTAs (re-pointed to the partner-network flow) */}
        <div className="flex flex-col gap-4 md:justify-self-end md:max-w-md w-full">
          <a
            href="/pws-partner"
            data-testid="pws-presentation-partner-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-lg transition-colors"
          >
            <CheckCircle2 className="w-5 h-5" />
            Request the Partner Guide
          </a>
          <a
            href="/pws-partner#interest"
            data-testid="pws-presentation-interest-cta"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border border-slate-700 hover:border-cyan-500 text-white font-semibold text-lg transition-colors"
          >
            Register your interest
          </a>
          <div className="text-xs text-slate-500 text-center md:text-left mt-2">
            Or visit{" "}
            <a href="/pws-partner" className="text-cyan-400 hover:underline">
              awarewatertesting.com.au/pws-partner
            </a>
          </div>
        </div>

        {/* Right: QR card — presentation-themed frame, plain B/W QR core with generous quiet zone */}
        <div
          data-testid="pws-presentation-qr-card"
          className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-7 shadow-2xl md:justify-self-start"
        >
          <div className="bg-white rounded-2xl p-4 sm:p-5 flex items-center justify-center">
            <img
              src="/qr/pws-partner-conference.svg"
              alt="QR code — scan to open awarewatertesting.com.au/pws-partner"
              width="240"
              height="240"
              className="block w-[240px] h-[240px]"
              data-testid="pws-presentation-qr-image"
            />
          </div>
          <div className="mt-4 text-center">
            <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 mb-1.5">Scan me</div>
            <div className="text-sm text-slate-200 leading-snug max-w-[240px] mx-auto">
              Scan to receive the digital Partner Guide<br />and request more information
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  ),
};

/* ── Tiny presentational helpers used by the section renderers ── */

function Stat({ big, label }) {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
      <div className="font-display text-4xl font-bold text-cyan-400 mb-2">{big}</div>
      <div className="text-slate-300 text-sm leading-snug">{label}</div>
    </div>
  );
}

function Li({ children, small = false }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className={`${small ? "w-4 h-4 mt-0.5" : "w-5 h-5 mt-1"} text-cyan-400 flex-shrink-0`} />
      <span>{children}</span>
    </li>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
      <div className="text-cyan-400 text-sm font-mono mb-2">{n}</div>
      <div className="font-display text-xl font-bold mb-2">{title}</div>
      <div className="text-slate-300 text-sm leading-relaxed">{body}</div>
    </div>
  );
}

function JourneyStep({ n, icon: Icon, title, body }) {
  return (
    <div
      className="bg-slate-900 rounded-2xl p-3 sm:p-4 border border-slate-800 hover:border-cyan-800/70 transition-colors flex flex-col h-full"
      data-testid={`pws-presentation-journey-${n}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="w-8 h-8 rounded-lg bg-cyan-950 border border-cyan-900 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-cyan-400" />
        </div>
        <div className="text-cyan-500/70 text-[10px] font-mono">{n}</div>
      </div>
      <div className="font-display text-sm sm:text-base font-bold mb-1">{title}</div>
      <div className="text-slate-300 text-[11px] sm:text-xs leading-snug flex-1">{body}</div>
    </div>
  );
}

function Card({ icon: Icon, title, body }) {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 hover:border-cyan-800 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-cyan-950 border border-cyan-900 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-cyan-400" />
      </div>
      <div className="font-display text-lg font-bold mb-2">{title}</div>
      <div className="text-slate-300 text-sm leading-relaxed">{body}</div>
    </div>
  );
}

function Phase({ weeks, title, body }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-6 items-start border-l-2 border-cyan-800 pl-6">
      <div className="text-cyan-400 font-mono text-sm">{weeks}</div>
      <div>
        <div className="font-display text-xl font-bold mb-1">{title}</div>
        <div className="text-slate-300 text-sm leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Testimonial({ name, region, quote }) {
  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800">
      <Quote className="w-5 h-5 text-cyan-500 mb-3" />
      <p className="text-slate-200 italic mb-4 leading-relaxed">&ldquo;{quote}&rdquo;</p>
      <div className="text-sm font-semibold text-white">{name}</div>
      <div className="text-xs text-slate-500">{region}</div>
    </div>
  );
}
